import React, { useState, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, Check } from 'lucide-react';
import { Button } from './Button';
import { FadeIn } from './FadeIn';
import { trackEvent } from '../utils/metaPixel';
import { BONUSES, BonusDetailCard } from '../data/bonusesData';
type StackItem = {
  Icon?: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  image?: string;
  imageAlt?: string;
  label: string;
  value: string;
};
const STACK_ITEMS: StackItem[] = [{
  Icon: BookOpen,
  label: 'Cambio Hormonal: El Método del 1%',
  value: '$47'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/dLdnND6p7giAJGVK2DMHjg/Bono_1.png',
  imageAlt: 'Bono Protocolo SOS Antojos',
  label: 'Bono #1: Protocolo SOS Antojos',
  value: '$17'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/ay7dGFBXr4zUJytSYWyuQQ/Bono_2.png',
  imageAlt: 'Bono Recetario Hormonal de 15 minutos',
  label: 'Bono #2: Recetario Hormonal de 15 Min',
  value: '$17'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/mvifc5kRZitubq5wr4qfxg/Bono_3.png',
  imageAlt: 'Bono Checklist de Supervivencia Hormonal',
  label: 'Bono #3: Checklist de Supervivencia',
  value: '$17'
}];
/**
 * Offer / checkout section — the conversion focal point of the page.
 * Two-column on desktop:
 *  - LEFT  : the "book cover" visual (anchors authority + the product feels
 *           tangible even though it's an e-book).
 *  - RIGHT : the value stack with line items, expandable bonus details,
 *           total real value (strikethrough), final price and the primary
 *           CTA button — a self-contained editorial checkout card.
 */
export function Bundle() {
  const [expanded, setExpanded] = useState(false);
  const handleCheckout = () => {
    trackEvent('AddToCart', {
      content_name: 'El Método del 1% - E-Book',
      content_ids: ['53815682957678'],
      content_type: 'product',
      value: 6.36,
      currency: 'USD'
    });
    window.location.assign('https://vuelveavivir.com/cart/53815682957678:1');
  };
  return <section id="oferta" className="py-16 md:py-24 bg-sage scroll-mt-24" data-id="element-30">
      <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-31">
        <div className="text-center mb-12 md:mb-16" data-id="element-32">
          <FadeIn data-id="element-33">
            <span className="uppercase tracking-[0.2em] text-xs text-ink-light mb-4 block" data-id="element-34">
              La Guía Maestra
            </span>
            <h2 className="font-serif text-3xl md:text-5xl text-ink" data-id="element-35">
              Todo lo que necesitas para <em className="italic" data-id="element-36">sanar</em>.
            </h2>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12 items-start" data-id="element-37">
          {/* VISUAL — book cover */}
          <div className="lg:col-span-5 flex justify-center lg:sticky lg:top-28" data-id="element-38">
            <FadeIn className="relative z-10" data-id="element-39">
              <div className="aspect-[3/4] max-w-xs mx-auto bg-cream shadow-2xl flex flex-col items-center justify-center p-8 md:p-10 border border-white/50 relative overflow-hidden" data-id="element-40">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/40 to-transparent pointer-events-none" data-id="element-41"></div>
                <span className="uppercase tracking-[0.3em] text-[10px] text-ink-light mb-6" data-id="element-42">
                  El Cambio Hormonal
                </span>
                <h3 className="font-serif text-3xl md:text-4xl text-center text-ink leading-tight mb-6" data-id="element-43">
                  El Método
                  <br data-id="element-44" />
                  del 1%
                </h3>
                <div className="w-12 h-[1px] bg-sand mb-6" data-id="element-45"></div>
                <p className="text-xs uppercase tracking-widest text-ink-light text-center" data-id="element-46">
                  + 3 Bonos Exclusivos
                </p>
              </div>
            </FadeIn>
          </div>

          {/* VALUE STACK & PRICING */}
          <div id="precio" className="lg:col-span-7 scroll-mt-32" data-id="element-47">
            <FadeIn delay={0.2} data-id="element-48">
              <div className="bg-cream border border-ink/5 shadow-soft-lg" data-id="element-49">
                {/* Main product row (the e-book) */}
                <ul data-id="element-50">
                  {STACK_ITEMS.slice(0, 1).map((item, i) => <li key={i} className="flex items-start gap-3 md:gap-4 px-5 md:px-8 py-4 md:py-5" data-id="element-51">
                      <div className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-md overflow-hidden bg-sage flex items-center justify-center mt-0.5" data-id="element-52">
                        {item.Icon ? <item.Icon strokeWidth={1.5} className="w-4 h-4 md:w-5 md:h-5 text-ink" data-id="element-53" /> : null}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-54">
                        <p className="text-sm md:text-base text-ink font-light leading-snug" data-id="element-55">
                          {item.label}
                        </p>
                        <p className="font-serif text-base md:text-lg text-ink-light mt-1 tabular-nums" translate="no" style={{
                      textDecoration: 'line-through',
                      textDecorationColor: '#722F37',
                      textDecorationThickness: '2px'
                    }} data-id="element-56">
                          {item.value}
                        </p>
                      </div>
                    </li>)}
                </ul>

                {/* Bonuses (3) + total + final price grouped into one
                 sand-tinted block so they read as a single offer. */}
                <div className="bg-sand/10 border-t-2 border-sand/40" data-id="element-57">
                  <div className="px-5 md:px-8 pt-5 md:pt-6 pb-1" data-id="element-58">
                    <span className="uppercase tracking-[0.18em] text-[10px] md:text-xs font-medium text-bordo" data-id="element-59">
                      + Bonos exclusivos para ti
                    </span>
                  </div>
                  <ul className="divide-y divide-sand/30" data-id="element-60">
                    {STACK_ITEMS.slice(1).map((item, i) => <li key={i} className="flex items-start gap-3 md:gap-4 px-5 md:px-8 py-4" data-id="element-61">
                        <div className="flex-shrink-0 w-9 h-9 rounded-md overflow-hidden bg-sage flex items-center justify-center mt-0.5 md:hidden" data-id="element-62">
                          {item.image ? <img src={item.image} alt={item.imageAlt || ''} width={500} height={500} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-63" /> : null}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-64">
                          <p className="text-sm md:text-base text-ink font-light leading-snug" data-id="element-65">
                            {item.label}
                          </p>
                          <p className="font-serif text-base md:text-lg text-ink-light mt-1 tabular-nums" translate="no" style={{
                        textDecoration: 'line-through',
                        textDecorationColor: '#722F37',
                        textDecorationThickness: '2px'
                      }} data-id="element-66">
                            {item.value}
                          </p>
                        </div>
                      </li>)}
                  </ul>

                  {/* Expand bonus details */}
                  <div className="border-t border-sand/40 px-5 md:px-8 py-4 flex justify-center" data-id="element-67">
                    <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} aria-controls="bonos-detalle" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-bordo hover:text-bordo-dark border-b border-bordo/40 hover:border-bordo pb-1 transition-colors duration-300" data-id="element-68">
                      <span data-id="element-69">
                        {expanded ? 'Leer menos' : 'Leer más sobre los bonos'}
                      </span>
                      <ChevronDown strokeWidth={1.5} className={`w-3.5 h-3.5 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} data-id="element-70" />
                    </button>
                  </div>

                  <AnimatePresence initial={false} data-id="element-71">
                    {expanded && <motion.div id="bonos-detalle" key="bonus-details" initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.4,
                    ease: [0.21, 0.47, 0.32, 0.98]
                  }} className="overflow-hidden border-t border-sand/40" data-id="element-72">
                        <div className="px-5 md:px-8 py-8 md:py-10 bg-cream space-y-8 md:space-y-10" data-id="element-73">
                          {BONUSES.map(bonus => <BonusDetailCard key={bonus.number} bonus={bonus} data-id="element-74" />)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* Total real value */}
                  <div className="px-5 md:px-8 py-4 md:py-5 border-t-2 border-sand/40 flex items-center justify-between" data-id="element-75">
                    <span className="uppercase tracking-[0.18em] text-[10px] md:text-xs text-ink-light" data-id="element-76">
                      Valor total real
                    </span>
                    <span className="font-serif text-xl md:text-2xl text-ink-light tabular-nums" translate="no" style={{
                    textDecoration: 'line-through',
                    textDecorationColor: '#722F37',
                    textDecorationThickness: '2px'
                  }} data-id="element-77">
                      $98
                    </span>
                  </div>

                  {/* Final price + CTA */}
                  <div className="px-5 md:px-8 py-7 md:py-9 border-t-2 border-sand/40 bg-sand/20 flex flex-col items-center text-center" data-id="element-78">
                    <span className="uppercase tracking-[0.18em] text-[10px] md:text-xs font-medium text-bordo mb-3" data-id="element-79">
                      Tu inversión hoy
                    </span>
                    <span className="font-serif text-5xl md:text-6xl text-bordo leading-none mb-3 tabular-nums" aria-label="precio actual 6 punto 36" translate="no" data-id="element-80">
                      <span data-id="element-81">$</span>
                      <span data-id="element-82">6</span>
                      <span data-id="element-83">.</span>
                      <span data-id="element-84">3</span>
                      <span data-id="element-85">6</span>
                    </span>
                    <p className="text-[11px] md:text-sm text-ink-light uppercase tracking-widest mb-6 md:mb-7" data-id="element-86">
                      Pago único · Acceso de por vida
                    </p>

                    <Button onClick={handleCheckout} className="w-full sm:w-auto" data-id="element-87">
                      Obtener Acceso Inmediato
                    </Button>

                    {/* Quick reassurance row */}
                    <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] uppercase tracking-[0.16em] text-ink-light" data-id="element-88">
                      <li className="inline-flex items-center gap-1.5" data-id="element-89">
                        <Check strokeWidth={2} className="w-3.5 h-3.5 text-sand-dark" data-id="element-90" />
                        Acceso inmediato
                      </li>
                      <li className="inline-flex items-center gap-1.5" data-id="element-91">
                        <Check strokeWidth={2} className="w-3.5 h-3.5 text-sand-dark" data-id="element-92" />
                        Para siempre
                      </li>
                      <li className="inline-flex items-center gap-1.5" data-id="element-93">
                        <Check strokeWidth={2} className="w-3.5 h-3.5 text-sand-dark" data-id="element-94" />
                        Garantía 30 días
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm md:text-base text-ink-light font-light leading-relaxed mt-6 md:mt-7 max-w-xl mx-auto" data-id="element-95">
                Ahorras{' '}
                <span className="font-medium text-ink" translate="no" data-id="element-96">
                  $91.64
                </span>{' '}
                hoy, y recibes los tres bonos completamente gratis solo por
                unirte ahora.
              </p>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>;
}